import { ActionFunction, json, type LoaderFunction, type MetaFunction } from "@remix-run/node"
import { Form, useActionData, useNavigation } from "@remix-run/react"
import { getUserByEmailAndPassword } from "~/src/logic/UsersDao";
import { commitUserSession, createUserSession, getUserId, getUserSession } from "~/src/logic/UserSessionLogic";
import { FORGOT_PASSWORD_URI_PATH, M_URI_FULL_ROOT_PATH, REGISTER_URI_PATH } from "~/src/types/openTypes";
import { Lock, Mail } from "lucide-react";


export const meta: MetaFunction = () => {
  return [
    { title: "Log in - WayPick" },
    {
      name: "description",
      content: "Log in to account."
    },
  ];
};

export const action: ActionFunction = async ({ request }) => {
  const form = await request.formData()
  const email = String(form.get("email"))
  const password = String(form.get("password"))

  if (!email || !password) {
    return json({ error: "Email and password are required." }, { status: 400 })
  }

  const user = await getUserByEmailAndPassword(email, password)
  if (!user) {
    return json({ error: "Invalid email or password." }, { status: 400 })
  }

  const userSession = await getUserSession(request);
  console.log("userSession", userSession.data);
  const redirectTo = userSession.get('loginReferer') || M_URI_FULL_ROOT_PATH
  console.log("redirectTo", redirectTo);

  return createUserSession(user.id.toString(), redirectTo)
}

export const loader: LoaderFunction = async ({ request }) => {
  const referer = request.headers.get("referer")

  const userSession = await getUserSession(request);
  userSession.unset('loginReferer');

  console.log("userSession - loader", userSession.data);
  let redirectTo = M_URI_FULL_ROOT_PATH
  if (referer) {
    try {
      const refererUrl = new URL(referer)
      console.log("refererUrl", refererUrl);
      const siteUrl = new URL(request.url);
      console.log("siteUrl", siteUrl);

      const isSameOrigin =
        refererUrl.origin === siteUrl.origin &&
        // Не редиректим обратно на login
        !refererUrl.pathname.includes("/login")

      if (isSameOrigin) {
        redirectTo = refererUrl.pathname + refererUrl.search + refererUrl.hash
        userSession.set('loginReferer', redirectTo);
        console.log("loginReferer", userSession.data);
      }
    } catch (e) {
      // Игнорируем ошибочные referer
    }
  }

  const userId = await getUserId(request)
  if (userId) {
    return new Response(null, {
      status: 302,
      headers: {
        Location: redirectTo,
      },
    })
  }

  return json({}, {
      headers: {
        "Set-Cookie": await commitUserSession(userSession)
      }
    }
  );
}


export default function Login() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[5fr_7fr]">
      {/* Left panel: login form */}
      <div className="flex items-center justify-center bg-white px-6 py-12 sm:px-8 md:px-12">
        <div className="w-full max-w-md space-y-6 mb-20">
          {/* Mobile greeting */}
          <div className="block md:hidden text-center mb-4">
            <h2 className="text-2xl font-bold">Welcome to WayPick</h2>
            <p className="text-gray-500 text-sm mt-2 leading-relaxed">
              Build your perfect sightseeing route and explore cities like a local.
            </p>
          </div>

          <div className="text-center">
            <h1 className="text-2xl font-semibold">Log In</h1>
            <p className="text-gray-500 text-sm">Access your personalized travel routes</p>
          </div>

          {actionData?.error && (
            <p className="text-red-600 text-sm text-center">{actionData.error}</p>
          )}

          <Form
            method="post"
            className="space-y-4"
          >
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <Mail className="w-4 h-4" />
                </span>
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  required
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <Lock className="w-4 h-4" />
                </span>
                <input
                  type="password"
                  name="password"
                  placeholder="••••••••"
                  required
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full text-white py-2 rounded-md text-sm transition duration-200 ${
                isSubmitting
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? "Logging in..." : "Log In"}
            </button>
          </Form>

          <div className="text-center text-sm text-gray-600 space-y-2">
            {/*<a*/}
            {/*  href={FORGOT_PASSWORD_URI_PATH}*/}
            {/*  className="text-blue-600 hover:underline"*/}
            {/*>*/}
            {/*  Forgot your password?*/}
            {/*</a>*/}
            <div>
              Don’t have an account?{" "}
              <a
                href={REGISTER_URI_PATH}
                className="text-blue-600 hover:underline"
              >
                Sign Up
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel: text only, no images */}
      <div className="hidden md:flex flex-col justify-center bg-blue-600 text-white p-10">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">Explore with WayPick</h2>
          <p className="text-lg leading-relaxed">
            Discover personalized sightseeing routes across museums, architecture, monuments, farms, wineries and more.
            Tailor your journey to your passions and travel like a local.
          </p>
        </div>
      </div>
    </div>
  );
}
